import { createUuid } from '@notifi/uuid/browser';
import type { CreateProposalDto, ProposalOption, UpdateProposalDto } from '@/types/proposal';
import type { EditableLineItem, OptionState } from '@/components/organisms/OptionCard';
import type { ContactAddress } from '@/types/contact';
import { addressesMatch, type AddressPartsLike } from '@/lib/address-match';
import { normalizeDisplayConfig } from '@/types/pricebook';
import {
  computeEstimatePricingPreview,
  type EstimatePricingMembershipInfo,
  type EstimatePricingPreviewLine,
  type EstimatePricingPreviewResult,
} from '@/lib/sales/estimate-pricing-preview';
import { hasReadableContent } from '@/lib/terms/clientTerms';
import { toTermsColumnPayload, type TermsSelection } from '@/lib/terms';
import { planToEstimateMembershipInfo } from '@/hooks/estimates/useMembershipInfoWithInCart';
import {
  combineEstimateMembershipInfo,
  type EstimateMembershipInfo,
} from '@/hooks/estimates/useEstimateMembershipInfo';
import type { MembershipPlan } from '@/components/organisms/pricebook/support/membership-plan-shared';
import { membershipPlanSummaryFromPurchaseSnapshot } from '@/lib/membership/format-plan-summary';

/**
 * What the "choose how to start" picker must re-send when it navigates.
 *
 * The picker lives on /dashboard/proposals/new and navigates to that SAME
 * route with a `template` key, so anything already in the URL is replaced, not
 * merged. A job that opened the picker put `sourceJobId` there; forgetting to
 * forward it loses the job → proposal link with no error. Mirrors the estimate
 * picker's `carry` in routes/.../estimates/new.tsx.
 */
export function proposalPickerCarry(input: {
  sourceJobId?: string;
  contactId?: string;
  locationId?: string;
  businessUnitId?: string | null;
}): {
  sourceJobId?: string;
  contactId?: string;
  locationId?: string;
  businessUnitId?: string;
} {
  return {
    ...(input.contactId ? { contactId: input.contactId } : {}),
    ...(input.locationId ? { locationId: input.locationId } : {}),
    ...(input.sourceJobId ? { sourceJobId: input.sourceJobId } : {}),
    ...(input.businessUnitId ? { businessUnitId: input.businessUnitId } : {}),
  };
}

/**
 * Whether the proposal form's write controls are locked.
 *
 * A create-only lock is not enough once Save & Send exists. That flow outlives
 * the create request — the draft POST settles, the channel modal opens, the
 * operator confirms, the send fires, and the redirect follows — so a lock keyed
 * on `createProposal.isPending` releases at the first step and leaves the rest
 * clickable. On a slow network that is a second proposal, not a flicker.
 *
 * `flowPending` covers the stretches where no mutation is pending at all: while
 * the modal is open, and between the send resolving and the navigation
 * settling. It is set once a submit is committed and released only on error,
 * so the lock holds until the form unmounts. Mirrors useEstimateSubmitFlow.
 */
export function proposalSubmitLocked(flags: {
  creating: boolean;
  updating: boolean;
  sending: boolean;
  flowPending: boolean;
}): boolean {
  return flags.creating || flags.updating || flags.sending || flags.flowPending;
}

/**
 * Why the proposal form refuses to save, in the order the operator should act.
 *
 * Rule order is the whole point, and it mirrors `evaluateEstimateSubmitGate`:
 * CUSTOMER_REQUIRED comes first there, worded "Please select a contact", so the
 * greyed-out button names the one thing the operator must do rather than a
 * background fetch they cannot influence. Without a customer rule here the
 * message fell through to the send-specific one and reported that the customer
 * had no email — about a customer that had not been chosen yet.
 *
 * Null means saving is allowed; a send-only blocker is a separate question.
 */
export function proposalSaveBlockedReason(input: {
  hasCustomer: boolean;
  optionCount: number;
  everyOptionNamed: boolean;
  everyOptionHasRequiredItem: boolean;
  everyCustomItemNamed: boolean;
  membershipPricingError: boolean;
  membershipPricingSettled: boolean;
}): string | null {
  if (!input.hasCustomer) return 'Please select a contact';
  if (input.optionCount < MIN_PROPOSAL_OPTIONS) {
    return `A proposal needs at least ${MIN_PROPOSAL_OPTIONS} options`;
  }
  if (!input.everyOptionNamed) return 'Give every option a name';
  if (!input.everyOptionHasRequiredItem) return 'Every option needs at least one required item';
  if (!input.everyCustomItemNamed) return 'Name each custom item';
  if (input.membershipPricingError) {
    return 'Membership pricing is unavailable. Retry before saving.';
  }
  if (!input.membershipPricingSettled) {
    return 'Waiting for membership pricing to finish loading.';
  }
  return null;
}

export const MIN_PROPOSAL_OPTIONS = 2;
export const MAX_PROPOSAL_OPTIONS = 4;

type OptionForPricing = Pick<
  OptionState,
  'items' | 'pricingMode' | 'discountType' | 'discountValue'
>;

export interface ProposalOptionPricing {
  subtotal: number;
  discountAmount: number;
  /** Post-discount, post-member total (proposals carry no option-level tax). */
  total: number;
  /** NOT-604 — `total` minus any MEMBERSHIP line: the deposit's real base. */
  depositEligibleTotal: number;
  /** Index-aligned with option.items. */
  lineItemPreviews: {
    lineSubtotal: number;
    lineTax: number;
    lineTotal: number;
    membershipDiscountAmount?: number;
    lineAfterDiscount?: number;
    documentDiscountAmount?: number;
    taxSnapshot: Record<string, unknown> | null;
  }[];
  membershipDiscountPreview: number;
  membershipEligibleRowIds: Set<string>;
}

/**
 * THE price of a proposal option, in the browser.
 *
 * One helper feeds the option card, the header/delta, the save gate and the
 * create/update payload, so all four state the same number.
 *
 * OWNER RULING 2026-08-24 — this runs EVERY option through the shared pricing
 * engine, members and non-members alike. It used to take a legacy list-price
 * branch (`calcOptionTotals`) for a non-member, because the engine disagrees
 * with that math in five ways and using it would re-price existing proposals.
 * It does re-price them, deliberately: a proposal option is now priced by the
 * engine that BILLS — the one estimates, jobs and invoices already use — and
 * the server derives the same figures independently from the option's own
 * lines (`proposals/support/proposal-option-pricing.ts`). The five divergences
 * and their before/after numbers are pinned in `option-pricing-parity.test.ts`.
 *
 * `membershipInfo` null now simply means no member rate applies. It is no
 * longer a different arithmetic.
 */
export function computeProposalOptionPreview(
  option: OptionForPricing,
  membershipInfo: EstimatePricingMembershipInfo | null,
): ProposalOptionPricing {
  const lineItems: EstimatePricingPreviewLine[] = option.items.map((item) => ({
    _rowId: item.rowId ?? item.lineItemId,
    lineItemId: item.lineItemId,
    type: item.type,
    quantity: item.quantity,
    basePrice: item.basePrice,
    laborRate: item.laborRate ?? 0,
    costBasis: item.costBasis ?? 0,
    markupPercent: item.markupPercent ?? 0,
    priceStrategy: item.priceStrategy,
    // The operator's own unit price for this line, which the operations engine
    // charges ahead of any strategy. Without it the proposal totals a line at
    // its catalog price while the invoice bills the override.
    manualPriceOverride: item.manualPriceOverride ?? null,
    taxable: item.taxable ?? false,
    taxLineType: item.taxLineType ?? null,
    estimatedHours: item.estimatedHours,
    chargeable: item.chargeable,
    pricingTreatment: item.pricingTreatment ?? null,
    markupOverride: item.markupOverride ?? null,
    dynamicPricingConfig: item.dynamicPricingConfig ?? null,
    isOptional: item.isOptional ?? false,
    isSelected: item.isSelected ?? true,
    // NOT-590 — the service <-> component linkage. The engine reads it to
    // decide that a declined service takes its parts down with it, and that a
    // ROLLED_UP bundle is ONE taxable sale. Withheld here, the card shows a
    // part still being charged under a service the customer declined.
    lineKey: item.lineKey ?? null,
    parentLineKey: item.parentLineKey ?? null,
    componentDisplayMode: item.componentDisplayMode ?? null,
    // NOT-605 — item-level member pricing must reach the engine so a proposal
    // prices a member line at parity with an estimate (memberPrice wins over the
    // plan %), not just the plan discount.
    memberPrice: item.memberPrice ?? null,
    memberDiscountPercent: item.memberDiscountPercent ?? null,
  }));
  const preview: EstimatePricingPreviewResult = computeEstimatePricingPreview({
    lineItems,
    taxResolution: null,
    pricingMode: option.pricingMode,
    // The option-level discount maps to the engine's document discount.
    discountType: option.discountType === 'FLAT' ? '$' : '%',
    discountValue: option.discountType ? (option.discountValue ?? 0) : 0,
    membershipInfo,
  });
  return {
    subtotal: preview.subtotal,
    discountAmount: preview.discountAmount,
    total: preview.grandTotal,
    // NOT-604 — the option's total minus any membership line inside it: what a
    // deposit is actually charged on, and so what the deposit bound judges
    // against. The same number the server derives for the same option.
    depositEligibleTotal: preview.depositEligibleTotal,
    lineItemPreviews: preview.lineItemPreviews.map((p) => ({
      lineSubtotal: p.lineSubtotal,
      lineTax: p.lineTax,
      lineTotal: p.lineTotal,
      membershipDiscountAmount: p.membershipDiscountAmount,
      lineAfterDiscount: p.lineAfterDiscount,
      documentDiscountAmount: p.documentDiscountAmount,
      taxSnapshot: p.taxSnapshot as Record<string, unknown> | null,
    })),
    membershipDiscountPreview: preview.membershipDiscountPreview,
    membershipEligibleRowIds: preview.membershipEligibleRowIds,
  };
}

/**
 * NOT-605 — resolve the membership context for ONE proposal option (join-&-save).
 * Active memberships apply to every option. A distinct plan sold inside one
 * option joins that option's pricing context, so complementary benefits can apply
 * immediately. The engine picks the best benefit per line and never stacks them.
 */
export function resolveOptionMembershipInfo(
  option: Pick<OptionForPricing, 'items'>,
  activeMembershipInfo: EstimateMembershipInfo | null,
  plansById: Map<string, MembershipPlan>,
): EstimateMembershipInfo | null {
  const membershipLine = option.items.find(
    (item) =>
      String(item.type ?? '').toUpperCase() === 'MEMBERSHIP' &&
      item.isSelected !== false &&
      Boolean(item.membershipPlanId),
  );
  const plan = membershipLine?.membershipPlanId
    ? plansById.get(membershipLine.membershipPlanId)
    : null;
  return combineEstimateMembershipInfo([
    activeMembershipInfo,
    plan ? planToEstimateMembershipInfo(plan) : null,
  ]);
}

/**
 * The Save gate for the option discount/deposit bounds — the SAME rule the
 * option card states inline, and the same one `proposals.service` asserts, so
 * the three never disagree. Returns the first offending option's message, or
 * null when every option is within its bounds.
 *
 * Judged PER OPTION against that option's own subtotal / deposit-eligible
 * total: Good, Better and Best each carry their own discount and deposit, and a
 * bound measured against an aggregate would pass a figure that is oversized for
 * the option actually charging it. Both bases come from
 * `computeProposalOptionPreview` — the shared engine's numbers, the same ones
 * the server independently derives and judges.
 *
 * Strict `>`, so an exact-subtotal discount and a whole-total deposit are both
 * allowed. The deposit bound is a rule about INPUT — a stored oversized value
 * stays saveable (`storedDeposit*` is the baseline), a deposit that is OFF is
 * never charged, and only the two stated types are judged.
 */
export function findProposalOptionBoundError(
  options: OptionState[],
  activeMembershipInfo: EstimateMembershipInfo | null,
  membershipPlansById: Map<string, MembershipPlan>,
): string | null {
  for (const option of options) {
    const { subtotal, depositEligibleTotal } = computeProposalOptionPreview(
      option,
      resolveOptionMembershipInfo(option, activeMembershipInfo, membershipPlansById),
    );
    const label = option.label || 'This option';
    const discountValue = option.discountValue ?? 0;
    if (option.discountType === 'PERCENT' && discountValue > 100) {
      return `Discount in "${label}" can’t exceed 100%.`;
    }
    if (option.discountType === 'FLAT' && discountValue > subtotal) {
      return `Discount in "${label}" can’t exceed the subtotal.`;
    }
    // Switching a stored-OFF deposit ON is a deposit this save SETS, at the
    // stored figures or any other — the server's own third clause
    // (`option.depositRequired === true && !storedOption.depositRequired`).
    // Judged on type and value alone, an option holding a switched-off 500%
    // deposit read as "unchanged" the instant it was switched on, and the save
    // the gate let through is the one the server refuses. Switching it OFF
    // re-sends the stored figure, which the server does not judge.
    // Not gated on `depositRequired`: an optional deposit is still quoted and
    // collected after approval (NOT-716).
    const depositChanged =
      option.storedDepositType === undefined ||
      (option.depositRequired && !option.storedDepositRequired) ||
      option.depositType !== option.storedDepositType ||
      option.depositValue !== option.storedDepositValue;
    if (!depositChanged) continue;
    if (option.depositType === '%' && option.depositValue > 100) {
      return `Deposit in "${label}" can’t exceed 100%.`;
    }
    if (option.depositType === '$' && option.depositValue > depositEligibleTotal) {
      return `Deposit in "${label}" can’t exceed the total.`;
    }
  }
  return null;
}

export function mapProposalLineItemToEditable(
  raw: Record<string, unknown>,
  legacySourceIndex?: number,
): EditableLineItem {
  const isMembershipLine = String(raw.type ?? '').toUpperCase() === 'MEMBERSHIP';
  const customerChoosesMembershipBilling =
    isMembershipLine && raw.membershipCustomerChoosesBillingOption === true;
  return {
    rowId: raw.rowId != null ? String(raw.rowId) : undefined,
    ...(isMembershipLine && raw.rowId == null && legacySourceIndex != null
      ? { legacySourceIndex }
      : {}),
    lineItemId: String(raw.lineItemId ?? ''),
    ...(Array.isArray(raw.equipmentIds)
      ? {
          equipmentIds: raw.equipmentIds.filter((id): id is string => typeof id === 'string'),
        }
      : {}),
    equipmentLocationOverrideReason:
      raw.equipmentLocationOverrideReason != null
        ? String(raw.equipmentLocationOverrideReason)
        : undefined,
    itemVersion: Number(raw.itemVersion ?? 1),
    name: String(raw.name ?? ''),
    code: String(raw.code ?? ''),
    type: String(raw.type ?? ''),
    unit: String(raw.unit ?? 'each'),
    description: String(raw.description ?? ''),
    imageUrl: typeof raw.imageUrl === 'string' ? raw.imageUrl : undefined,
    imagePreviewUrl: typeof raw.imagePreviewUrl === 'string' ? raw.imagePreviewUrl : undefined,
    basePrice: Number(raw.basePrice ?? 0),
    priceStrategy: String(raw.priceStrategy ?? 'FLAT'),
    taxable: Boolean(raw.taxable ?? false),
    quantity: Number(raw.quantity ?? 1),
    // Customer billing choice also delegates whether to buy the membership.
    // Repair legacy snapshots before they enter editor state so an unrelated
    // proposal save cannot send the now-invalid required/preselected pairing.
    isOptional: customerChoosesMembershipBilling ? true : Boolean(raw.isOptional ?? false),
    isSelected: customerChoosesMembershipBilling ? false : Boolean(raw.isSelected ?? true),
    laborRate: Number(raw.laborRate ?? 0),
    costBasis: Number(raw.costBasis ?? 0),
    markupPercent: Number(raw.markupPercent ?? 0),
    estimatedHours: raw.estimatedHours != null ? Number(raw.estimatedHours) : undefined,
    taxLineType: raw.taxLineType != null ? String(raw.taxLineType) : null,
    taxCategory: raw.taxCategory != null ? String(raw.taxCategory) : null,
    taxCode: raw.taxCode != null ? String(raw.taxCode) : null,
    glAccount: raw.glAccount != null ? String(raw.glAccount) : null,
    glCogs: raw.glCogs != null ? String(raw.glCogs) : null,
    glExpenseAccount: raw.glExpenseAccount != null ? String(raw.glExpenseAccount) : null,
    glClass: raw.glClass != null ? String(raw.glClass) : null,
    pricebookTaxable: typeof raw.pricebookTaxable === 'boolean' ? raw.pricebookTaxable : undefined,
    displayConfig: normalizeDisplayConfig(raw.displayConfig),
    isCustom: raw.source === 'MANUAL' || raw.isCustom === true || !String(raw.code ?? '').trim(),
    chargeable: typeof raw.chargeable === 'boolean' ? raw.chargeable : undefined,
    pricingTreatment:
      raw.pricingTreatment === 'included_in_service' || raw.pricingTreatment === 'separate_charge'
        ? raw.pricingTreatment
        : null,
    markupOverride: raw.markupOverride != null ? Number(raw.markupOverride) : null,
    hiddenFromCustomer:
      typeof raw.visibleToCustomer === 'boolean'
        ? !raw.visibleToCustomer
        : raw.hiddenFromCustomer === true,
    visibleToCustomer:
      typeof raw.visibleToCustomer === 'boolean' ? raw.visibleToCustomer : undefined,
    laborCostRate: raw.laborCostRate != null ? Number(raw.laborCostRate) : undefined,
    laborCostLines: Array.isArray(raw.laborCostLines)
      ? (raw.laborCostLines as import('@/types/pricebook').ServiceLaborLine[])
      : undefined,
    dynamicPricingConfig:
      raw.dynamicPricingConfig && typeof raw.dynamicPricingConfig === 'object'
        ? (raw.dynamicPricingConfig as import('@/types/pricebook').DynamicServicePricingConfig)
        : undefined,
    // The operator's own unit price for this line. It has to survive the
    // load→edit→save round trip like every other pricing field: dropped here,
    // reopening a saved proposal re-prices the line at its CATALOG price and
    // the next save stores that — a $400 quote silently becoming $1,000.
    manualPriceOverride: raw.manualPriceOverride != null ? Number(raw.manualPriceOverride) : null,
    // NOT-605 — item-level member pricing must survive the load→edit→save round
    // trip, else reopening a saved proposal re-prices member lines at the plan %
    // only (dropping the item memberPrice) and the total silently shifts.
    memberPrice: raw.memberPrice != null ? Number(raw.memberPrice) : null,
    memberDiscountPercent:
      raw.memberDiscountPercent != null ? Number(raw.memberDiscountPercent) : null,
    membershipPlanId: raw.membershipPlanId != null ? String(raw.membershipPlanId) : undefined,
    membershipSetupFee:
      Number(
        (raw.membershipPurchaseSnapshot as { planSnapshot?: { setupFee?: number } } | undefined)
          ?.planSnapshot?.setupFee ?? raw.membershipSetupFee,
      ) || 0,
    membershipSetupFeeFor: raw.membershipSetupFeeFor as string | undefined,
    membershipSetupFeePlanId: raw.membershipSetupFeePlanId as string | undefined,
    membershipBillingOptionId:
      raw.membershipBillingOptionId != null ? String(raw.membershipBillingOptionId) : undefined,
    membershipCustomerChoosesBillingOption: raw.membershipCustomerChoosesBillingOption === true,
    membershipPlanSummary: membershipPlanSummaryFromPurchaseSnapshot(
      raw.membershipPurchaseSnapshot,
    ),
    // NOT-590 — the service ↔ component linkage must survive the load→edit→save
    // round trip. Dropping it here saved the service and its parts as unrelated
    // rows, losing the rollup and pricing the parts on a quote the operator set
    // to present a single amount.
    lineKey: raw.lineKey != null ? String(raw.lineKey) : undefined,
    parentLineKey: raw.parentLineKey != null ? String(raw.parentLineKey) : undefined,
    componentDisplayMode:
      raw.componentDisplayMode === 'ROLLED_UP' || raw.componentDisplayMode === 'ITEMIZED'
        ? raw.componentDisplayMode
        : undefined,
  };
}

export function createEmptyProposalOption(label: string): OptionState {
  return {
    clientId: createProposalOptionClientId(),
    label,
    description: '',
    items: [],
    expanded: false,
    recommended: false,
    pricingMode: 'STANDARD',
    discountType: 'FLAT',
    discountValue: 0,
    discountReason: '',
    depositType: '$',
    depositValue: 0,
    depositRequired: false,
  };
}

export function hydrateProposalOptions(
  rawOptions: Array<Record<string, unknown>>,
  defaults?: Partial<Pick<OptionState, 'color' | 'estimateTemplateId'>>,
  identityMode: 'persisted' | 'new' = 'persisted',
): OptionState[] {
  return rawOptions.map((rawOption, idx) => ({
    optionKey:
      identityMode === 'persisted' && rawOption.optionKey != null
        ? String(rawOption.optionKey)
        : undefined,
    ...(identityMode === 'persisted' && rawOption.optionKey == null
      ? { legacyOptionIndex: idx }
      : {}),
    clientId:
      identityMode === 'persisted' && rawOption.optionKey != null
        ? String(rawOption.optionKey)
        : createProposalOptionClientId(),
    label: String(rawOption.label ?? `Option ${idx + 1}`),
    description: String(rawOption.description ?? ''),
    color: defaults?.color ?? (rawOption.color != null ? String(rawOption.color) : undefined),
    estimateTemplateId:
      defaults?.estimateTemplateId ??
      (rawOption.estimateTemplateId != null ? String(rawOption.estimateTemplateId) : undefined),
    items: ((rawOption.lineItems ?? []) as Record<string, unknown>[]).map((lineItem, lineIndex) => {
      const editable = mapProposalLineItemToEditable(
        lineItem,
        identityMode === 'persisted' ? lineIndex : undefined,
      );
      return identityMode === 'new'
        ? {
            ...editable,
            rowId: createUuid(),
            legacySourceIndex: undefined,
          }
        : editable;
    }),
    expanded: false,
    recommended: Boolean(rawOption.recommended ?? false),
    pricingMode: rawOption.pricingMode === 'TM_OVERRIDE' ? 'TM_OVERRIDE' : 'STANDARD',
    discountType:
      rawOption.discountType === 'PERCENT' || rawOption.discountType === 'FLAT'
        ? rawOption.discountType
        : 'FLAT',
    discountValue: rawOption.discountValue != null ? Number(rawOption.discountValue) : 0,
    discountReason: rawOption.discountReason != null ? String(rawOption.discountReason) : '',
    depositType: rawOption.depositType === 'PERCENT' ? '%' : '$',
    depositValue: rawOption.depositValue != null ? Number(rawOption.depositValue) : 0,
    depositRequired: Boolean(rawOption.depositRequired ?? false),
    ...(rawOption.paymentScheduleTemplate !== undefined
      ? {
          paymentScheduleTemplate:
            rawOption.paymentScheduleTemplate as OptionState['paymentScheduleTemplate'],
        }
      : {}),
    // The baseline the deposit bound is judged against — the canonical form of
    // what this option HOLDS, which is exactly what we just hydrated. Only a
    // persisted option has one; a template-seeded ('new') option is new work,
    // so every figure in it is being set.
    ...(identityMode === 'persisted'
      ? {
          storedDepositType: (rawOption.depositType === 'PERCENT' ? '%' : '$') as '$' | '%',
          storedDepositValue: rawOption.depositValue != null ? Number(rawOption.depositValue) : 0,
          storedDepositRequired: Boolean(rawOption.depositRequired ?? false),
        }
      : {}),
  }));
}

export function createProposalOptionClientId(): string {
  return createUuid();
}

export function buildProposalPayload({
  contactId,
  customerName,
  customerEmail,
  customerPhone,
  customerAddress,
  serviceAddressMode,
  selectedServiceAddressIndex,
  billingMode,
  selectedBillingIndex,
  contactAddresses,
  saStreet,
  saStreet2,
  saCity,
  saState,
  saZip,
  saCountry = '',
  baStreet,
  baStreet2,
  baCity,
  baState,
  baZip,
  baCountry = '',
  validUntil,
  options,
  notes,
  terms,
  internalNotes,
  pendingAttachmentId,
  includePendingAttachmentId,
  includeTerms = true,
  includeIdentityClears = false,
  sourceJobId,
  activeMembershipInfo,
  membershipPlansById,
}: {
  contactId: string | null;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  customerAddress: string;
  serviceAddressMode: 'select' | 'manual';
  selectedServiceAddressIndex: number | null;
  billingMode: 'select' | 'manual';
  selectedBillingIndex: number | null;
  contactAddresses: ContactAddress[];
  saStreet: string;
  saStreet2: string;
  saCity: string;
  saState: string;
  saZip: string;
  /** A typed address has no country field; this is the one it was restored or
   *  seeded with (a stored proposal, a source job's snapshot), if any. */
  saCountry?: string;
  baStreet: string;
  baStreet2: string;
  baCity: string;
  baState: string;
  baZip: string;
  baCountry?: string;
  validUntil: string;
  options: OptionState[];
  notes: string;
  terms: TermsSelection;
  internalNotes: string;
  pendingAttachmentId?: string;
  includePendingAttachmentId?: boolean;
  /** Send the terms block? A proposal UPDATE omits it unless the operator
   *  changed the selection, so a notes-only save keeps the server's fast path
   *  and never re-resolves wording the customer has already seen. */
  includeTerms?: boolean;
  /** Proposal updates must distinguish an explicit clear from an omitted field. */
  includeIdentityClears?: boolean;
  // Link the proposal back to the one-off Job where it was created.
  sourceJobId?: string;
  // NOT-605 — the customer's ACTIVE membership (applies to every option), plus the
  // plans catalog to resolve a per-option in-cart membership (join-&-save). Each
  // option is priced through resolveOptionMembershipInfo, so the stored (and later
  // materialized) totals match what the customer is shown. Both omitted → list
  // price, unchanged behavior.
  activeMembershipInfo?: EstimateMembershipInfo | null;
  membershipPlansById?: Map<string, MembershipPlan>;
}): CreateProposalDto | UpdateProposalDto {
  const serviceAddress =
    serviceAddressMode === 'select'
      ? selectedServiceAddressIndex !== null
        ? (() => {
            const address = contactAddresses[selectedServiceAddressIndex];
            if (!address || !address.street || !address.city || !address.state || !address.zip) {
              return undefined;
            }
            return {
              street: String(address.street),
              ...(address.street2 && { street2: String(address.street2) }),
              city: String(address.city),
              state: String(address.state),
              zip: String(address.zip),
              // The row's own country — left out, the server stamps 'US' on a
              // Canadian site and the estimate and job inherit it.
              ...(address.country && { country: String(address.country) }),
              // Snapshot the saved address's tax-exemption status onto the
              // proposal so the tax engine resolves $0 tax for an exempt address.
              isTaxExempt: address.isTaxExempt ?? false,
              taxExemptCertificate: address.taxExemptCertificate ?? null,
              taxExemptExpiry: address.taxExemptExpiry ?? null,
              ...(address.id && { addressId: address.id }),
            };
          })()
        : undefined
      : saStreet.trim() && saCity.trim() && saState.trim() && saZip.trim()
        ? {
            street: saStreet.trim(),
            ...(saStreet2.trim() && { street2: saStreet2.trim() }),
            city: saCity.trim(),
            state: saState.trim(),
            zip: saZip.trim(),
            ...(saCountry.trim() && { country: saCountry.trim() }),
          }
        : undefined;

  const billingAddress =
    billingMode === 'manual'
      ? baStreet.trim() && baCity.trim() && baState.trim() && baZip.trim()
        ? {
            street: baStreet.trim(),
            ...(baStreet2.trim() && { street2: baStreet2.trim() }),
            city: baCity.trim(),
            state: baState.trim(),
            zip: baZip.trim(),
            ...(baCountry.trim() && { country: baCountry.trim() }),
          }
        : undefined
      : selectedBillingIndex !== null
        ? (() => {
            const address = contactAddresses[selectedBillingIndex];
            if (!address || !address.street || !address.city || !address.state || !address.zip) {
              return undefined;
            }
            return {
              street: String(address.street),
              ...(address.street2 && { street2: String(address.street2) }),
              city: String(address.city),
              state: String(address.state),
              zip: String(address.zip),
              ...(address.country && { country: String(address.country) }),
              ...(address.id && { addressId: address.id }),
            };
          })()
        : undefined;

  const customerBillingAddress = billingAddress
    ? [
        billingAddress.street,
        billingAddress.street2,
        billingAddress.city,
        billingAddress.state,
        billingAddress.zip,
      ]
        .filter(Boolean)
        .join(', ')
    : billingMode === 'manual'
      ? ''
      : customerAddress.trim();

  return {
    customer: {
      name: customerName.trim(),
      email: customerEmail.trim() || undefined,
      phone: customerPhone.trim() || undefined,
      address: customerBillingAddress || undefined,
      ...(contactId ? { contactId } : includeIdentityClears ? { contactId: null } : {}),
    },
    ...(serviceAddress
      ? { serviceAddress }
      : includeIdentityClears
        ? { serviceAddress: null }
        : {}),
    ...(billingAddress
      ? { billingAddress }
      : includeIdentityClears
        ? { billingAddress: null }
        : {}),
    options: options.map((option) => {
      // NOT-605 — price through the shared engine so member rates (active member,
      // or a membership sold in THIS option) land in the trusted payload;
      // identical to calcOptionTotals when the option resolves to no membership.
      const optionMembershipInfo = resolveOptionMembershipInfo(
        option,
        activeMembershipInfo ?? null,
        membershipPlansById ?? new Map(),
      );
      const preview = computeProposalOptionPreview(option, optionMembershipInfo);
      const subtotal = preview.subtotal;
      const total = preview.total;

      return {
        ...(option.legacyOptionIndex != null && {
          legacyOptionIndex: option.legacyOptionIndex,
        }),
        ...(option.optionKey && { optionKey: option.optionKey }),
        label: option.label,
        description: option.description || undefined,
        color: option.color,
        recommended: option.recommended,
        pricingMode: option.pricingMode,
        discountType: (option.discountType ?? null) as 'PERCENT' | 'FLAT' | null,
        discountValue: option.discountValue,
        discountReason: option.discountReason || undefined,
        depositType: option.depositType === '%' ? 'PERCENT' : 'FLAT',
        depositValue: option.depositValue,
        depositRequired: option.depositRequired,
        ...(option.paymentScheduleTemplate !== undefined
          ? { paymentScheduleTemplate: option.paymentScheduleTemplate }
          : {}),
        estimateTemplateId: option.estimateTemplateId ?? undefined,
        lineItems: option.items.map((item, itemIndex) => ({
          ...(item.legacySourceIndex != null && {
            legacySourceIndex: item.legacySourceIndex,
          }),
          ...(item.rowId && { rowId: item.rowId }),
          lineItemId: item.lineItemId,
          ...(Array.isArray(item.equipmentIds) ? { equipmentIds: item.equipmentIds } : {}),
          ...(item.equipmentLocationOverrideReason && {
            equipmentLocationOverrideReason: item.equipmentLocationOverrideReason,
          }),
          itemVersion: item.itemVersion,
          name: item.name,
          code: item.code,
          type: item.type,
          unit: item.unit || 'each',
          description: item.description,
          ...(item.imageUrl && { imageUrl: item.imageUrl }),
          basePrice: item.basePrice,
          laborRate: item.laborRate,
          costBasis: item.costBasis,
          markupPercent: item.markupPercent,
          currency: 'USD',
          priceStrategy: item.priceStrategy || 'FLAT',
          taxable: item.taxable ?? false,
          ...(typeof item.pricebookTaxable === 'boolean' && {
            pricebookTaxable: item.pricebookTaxable,
          }),
          taxLineType: item.taxLineType ?? null,
          taxCategory: item.taxCategory ?? null,
          taxCode: item.taxCode ?? null,
          glAccount: item.glAccount ?? null,
          glCogs: item.glCogs ?? null,
          glExpenseAccount: item.glExpenseAccount ?? null,
          glClass: item.glClass ?? null,
          displayConfig: normalizeDisplayConfig(item.displayConfig),
          quantity: item.quantity,
          source: item.isCustom ? 'MANUAL' : 'CATALOG',
          ...(typeof item.chargeable === 'boolean' && { chargeable: item.chargeable }),
          ...(item.pricingTreatment && { pricingTreatment: item.pricingTreatment }),
          ...(item.markupOverride != null && { markupOverride: item.markupOverride }),
          ...(item.hiddenFromCustomer === true && { hiddenFromCustomer: true }),
          ...(typeof item.visibleToCustomer === 'boolean' && {
            visibleToCustomer: item.visibleToCustomer,
          }),
          ...(item.laborCostRate != null && { laborCostRate: item.laborCostRate }),
          ...(item.laborCostLines?.length && { laborCostLines: item.laborCostLines }),
          ...(item.dynamicPricingConfig && {
            dynamicPricingConfig: item.dynamicPricingConfig,
          }),
          // ── The fields that DECIDE THE PRICE ────────────────────────────
          // The server prices the option from these now, so one that does not
          // travel is not a cosmetic loss: the line re-prices at the WRONG
          // number. The override in particular was previewed with and then
          // dropped, so it only ever survived inside the client-computed
          // `lineTotal` — a $400 override over a $1,000 catalog item re-priced
          // at $1,000.
          ...(item.manualPriceOverride != null && {
            manualPriceOverride: item.manualPriceOverride,
          }),
          // NOT-590 — the service <-> component linkage, without which a
          // declined service cannot take its parts with it.
          ...(item.lineKey && { lineKey: item.lineKey }),
          ...(item.parentLineKey && { parentLineKey: item.parentLineKey }),
          ...(item.componentDisplayMode && {
            componentDisplayMode: item.componentDisplayMode,
          }),
          // NOT-605 — persist item-level member pricing + the plan link so a saved
          // proposal reprices identically on reload and materializes at parity.
          ...(item.memberPrice != null && { memberPrice: item.memberPrice }),
          ...(item.memberDiscountPercent != null && {
            memberDiscountPercent: item.memberDiscountPercent,
          }),
          ...(item.membershipPlanId && { membershipPlanId: item.membershipPlanId }),
          membershipSetupFee: item.membershipSetupFee,
          membershipSetupFeeFor: item.membershipSetupFeeFor,
          membershipSetupFeePlanId: item.membershipSetupFeePlanId,
          ...(item.membershipBillingOptionId && {
            membershipBillingOptionId: item.membershipBillingOptionId,
          }),
          ...(item.membershipCustomerChoosesBillingOption === true && {
            membershipCustomerChoosesBillingOption: true,
          }),
          // The engine's per-line figures. ADVISORY on the wire: the server
          // re-derives all four from the line's own catalog snapshot fields
          // and overwrites them, so an inflated `lineTotal` buys a caller
          // nothing. They are still sent because the DTO requires the shape
          // and because a payload that states a different number from the one
          // that comes back is worth being able to see.
          //
          // The old `?? calcLineSubtotal(...)` fallbacks are gone with the
          // legacy math they belonged to: a second implementation of "what is
          // this line worth" is the exact defect this change exists to end.
          lineSubtotal: preview.lineItemPreviews[itemIndex]?.lineSubtotal ?? 0,
          lineTax: preview.lineItemPreviews[itemIndex]?.lineTax ?? 0,
          lineTotal: preview.lineItemPreviews[itemIndex]?.lineTotal ?? 0,
          taxSnapshot: preview.lineItemPreviews[itemIndex]?.taxSnapshot ?? null,
          // NOT-605 — the LIST per-line total (pre member discount) so the public
          // page can show list price + an explicit "Membership savings" line
          // instead of a net line that mismatches the list subtotal.
          lineListTotal: preview.lineItemPreviews[itemIndex]?.lineSubtotal ?? 0,
          isOptional:
            item.membershipCustomerChoosesBillingOption === true
              ? true
              : (item.isOptional ?? false),
          isSelected:
            item.membershipCustomerChoosesBillingOption === true
              ? false
              : (item.isSelected ?? true),
          ...(typeof item.estimatedHours === 'number' && { estimatedHours: item.estimatedHours }),
          ...(item.configurationName && { configurationName: item.configurationName }),
        })),
        subtotal,
        taxTotal: 0,
        total,
        // NOT-605 — persist the ACTUAL document discount the engine computed
        // (member-aware base, membership lines excluded) so the public breakdown
        // shows the right Discount / Membership-savings split instead of
        // re-deriving it from the LIST subtotal.
        discountAmount: preview.discountAmount,
      };
    }) as ProposalOption[],
    validUntil: validUntil || undefined,
    notes: notes.trim() || undefined,
    ...(includeTerms ? toTermsColumnPayload(terms) : {}),
    // ALWAYS sent, empty included: an operator who deletes the internal note
    // means to delete it, and `undefined` is dropped by JSON.stringify, so
    // `|| undefined` would make an emptied note come back on the next load.
    // The server stores blank as NULL.
    internalNotes: internalNotes.trim(),
    ...(includePendingAttachmentId && pendingAttachmentId ? { pendingAttachmentId } : {}),
    ...(sourceJobId ? { sourceJobId } : {}),
  };
}

/**
 * Does the proposal have anything to put in the "Notes, terms & documents"
 * group? Notes and terms are rich text, so this has to be asked of the readable
 * content: `'<p></p>'.trim()` is truthy, and the group's cards each judge
 * emptiness properly — so a raw check here renders the heading with nothing
 * underneath it.
 */
export function hasProposalNotesDocs(input: {
  notes?: string | null;
  terms?: string | null;
  publicAttachmentCount: number;
}): boolean {
  return (
    hasReadableContent(input.notes) ||
    hasReadableContent(input.terms) ||
    input.publicAttachmentCount > 0
  );
}

/**
 * NOT-586 — reopening a saved proposal has to find the contact address row its
 * stored address came from, or the picker comes up unselected and silently
 * discards the choice on the next save.
 *
 * The lookup used to compare fields with `===`, so a postal code stored
 * "V3M 0P2" against one typed "V3M0P2" — or a differently cased city — left
 * `findIndex` at -1. `addressesMatch` is the comparator the rest of the sales
 * surfaces already use.
 *
 * The lookup deliberately scans ALL rows, `isService` included. That flag is a
 * display/defaulting filter, not an eligibility rule — `schema.prisma` says "a
 * row can serve any combination", the only invariant the backend enforces is
 * `isPrimary ⇒ isService`, and nothing in the estimates, jobs or proposals
 * modules validates a stored service address against it.
 *
 * Gating here looks tighter but costs data. `new.tsx` seeds the edit form in
 * `manual` mode and only switches to `select` when this returns >= 0, so a
 * proposal saved against a row later un-flagged would stay manual — and the
 * manual branch of `buildProposalPayload` emits neither `addressId` nor the
 * `isTaxExempt` / `taxExemptCertificate` / `taxExemptExpiry` snapshot that the
 * selected-row branch carries. The next save would silently downgrade an
 * exempt, linked address to a one-off and start taxing it.
 *
 * Returning the off-list row instead is safe because `JobCustomerContactsSection`
 * surfaces a selected-but-unlisted row via `unofferedServiceOption`, so the
 * picker offers exactly the row this will save. Same remedy as the estimate and
 * job surfaces — do not "restore" the gate for symmetry with the helpers in
 * `new.tsx`, which pick a *default* and are a different question.
 *
 * `findProposalBillingAddressIndex` below uses the saved location id first.
 * Legacy billing snapshots without an id still use the billing flag and
 * address fields to find their row.
 */
export function findProposalServiceAddressIndex(
  addresses: readonly ContactAddress[],
  serviceAddress: AddressPartsLike | null | undefined,
): number {
  if (!serviceAddress) return -1;
  const addressId = serviceAddress.addressId;
  if (addressId?.trim()) {
    return addresses.findIndex((address) => address.id === addressId);
  }
  return addresses.findIndex((address) => addressesMatch(address, serviceAddress));
}

export function findProposalBillingAddressIndex(
  addresses: readonly ContactAddress[],
  billingAddress: AddressPartsLike | null | undefined,
): number {
  if (!billingAddress) return -1;
  const addressId = billingAddress.addressId;
  if (addressId?.trim()) {
    return addresses.findIndex((address) => address.id === addressId);
  }
  return addresses.findIndex(
    (address) => Boolean(address.isBilling) && addressesMatch(address, billingAddress),
  );
}

/**
 * Where one side of a proposal raised on a job should point: a saved row of the
 * customer's, or the job's own snapshot typed in as a manual entry. `null`
 * leaves the contact's default.
 */
export type SourceJobAddressPick =
  | { kind: 'saved'; index: number }
  | { kind: 'manual'; address: AddressPartsLike }
  | null;

interface SourceJobAddresses {
  locationId?: string | null;
  serviceAddress?: AddressPartsLike | null;
  billingAddress?: AddressPartsLike | null;
}

/**
 * The job's own row, by id only when the snapshot names one. A named row that
 * is no longer offered (the location was retired) must not fall through to a
 * lookalike at the same street, so the snapshot is kept as typed instead.
 */
function pickJobAddress(
  addresses: readonly ContactAddress[],
  snapshot: AddressPartsLike | null | undefined,
  rowId: string | null | undefined,
): SourceJobAddressPick {
  const id = rowId?.trim();
  if (id) {
    const index = addresses.findIndex((address) => address.id === id);
    if (index >= 0) return { kind: 'saved', index };
  } else if (snapshot) {
    const index = addresses.findIndex((address) => addressesMatch(address, snapshot));
    if (index >= 0) return { kind: 'saved', index };
  }
  return snapshot && (snapshot.street ?? snapshot.street1)?.trim()
    ? { kind: 'manual', address: snapshot }
    : null;
}

/**
 * A proposal raised on a job is quoted for that job's site and billed where the
 * job bills.
 *
 * The contact prefill picks the customer's DEFAULT rows, which for a customer
 * with more than one location is somewhere else: the proposal went out for the
 * primary site, and the estimate it became and the job after that inherited
 * it. The job already knows the answer, so it wins over the default.
 *
 * Billing follows the customer, so a job with no billing snapshot of its own
 * leaves the customer default alone; only a job billed somewhere else carries
 * that address over.
 */
export function sourceJobAddressPicks(
  addresses: readonly ContactAddress[],
  job: SourceJobAddresses | null | undefined,
): { service: SourceJobAddressPick; billing: SourceJobAddressPick } {
  if (!job) return { service: null, billing: null };
  return {
    service: pickJobAddress(
      addresses,
      job.serviceAddress,
      job.locationId ?? job.serviceAddress?.addressId,
    ),
    billing: job.billingAddress
      ? pickJobAddress(addresses, job.billingAddress, job.billingAddress.addressId)
      : null,
  };
}

/**
 * NOT-586 — which row the picker lands on when the form leaves manual entry.
 *
 * Two questions again, and only one of them is about `isService`:
 *
 *   1. "Is something already selected?" — if so it is the answer, whatever its
 *      flags. Reopening a proposal saved against a row since un-flagged as a
 *      service address restores that row (`findProposalServiceAddressIndex`)
 *      and the picker offers it back (`unofferedServiceOption`). Re-deciding
 *      it here on `isService` threw that away and silently selected a
 *      DIFFERENT place — the toggle also calls `applyServiceAddress`, so the
 *      street/city/state/zip fields were overwritten with the default row's.
 *      Nothing announced the swap; the proposal simply saved against an
 *      address the user never picked.
 *
 *   2. "Nothing is selected — which is the sensible default?" — that IS a
 *      display-preference question, so it keeps the `isService` filter and the
 *      primary-first preference. Offering a billing-only row as the default
 *      service address would be a worse guess than offering none.
 *
 * A selection that no longer resolves (the row was deleted) falls through to
 * the default, which is the same thing as having no selection.
 */
export function resolveProposalServiceAddressIndex(
  addresses: readonly ContactAddress[],
  currentIndex: number | null,
): number | null {
  const current = currentIndex !== null ? addresses[currentIndex] : undefined;
  if (current) return currentIndex;

  const primaryServiceIndex = addresses.findIndex(
    (address) => address.isService !== false && address.isPrimary,
  );
  if (primaryServiceIndex >= 0) return primaryServiceIndex;

  const serviceIndex = addresses.findIndex((address) => address.isService !== false);
  return serviceIndex >= 0 ? serviceIndex : null;
}

/**
 * Billing's twin, and it keeps the selection for the same reason: a row the
 * user is already pointed at is the answer regardless of its flags, and
 * `unofferedBillingOption` is what keeps the dropdown able to show it.
 *
 * The selectable set still drives the DEFAULT — billing rows when the contact
 * has any, service rows otherwise, primary first.
 */
export function resolveProposalBillingAddressIndex(
  addresses: readonly ContactAddress[],
  currentIndex: number | null,
): number | null {
  if (currentIndex !== null && addresses[currentIndex]) return currentIndex;

  const billingIndices = addresses.reduce<number[]>((indices, address, index) => {
    if (address.isBilling) indices.push(index);
    return indices;
  }, []);
  const serviceIndices = addresses.reduce<number[]>((indices, address, index) => {
    if (address.isService !== false) indices.push(index);
    return indices;
  }, []);
  const selectableIndices = billingIndices.length > 0 ? billingIndices : serviceIndices;

  const primaryIndex = selectableIndices.find((index) => addresses[index]?.isPrimary);
  return primaryIndex ?? selectableIndices[0] ?? null;
}
